/**
 * Location Permission Hook
 * Requests location permission on first visit and stores the status
 * Follows Single Responsibility Principle - handles only location permission
 */

import { useEffect, useState } from 'react';

const LOCATION_PERMISSION_KEY = 'nexgo_location_permission_requested';

export interface UseLocationPermissionResult {
  hasRequested: boolean;
  isGranted: boolean | null; // null = unknown, true = granted, false = denied
  requestPermission: () => void;
}

/**
 * Hook for managing location permission
 * Requests permission only once on first visit
 * @returns Permission status and request function
 */
export function useLocationPermission(): UseLocationPermissionResult {
  const [hasRequested, setHasRequested] = useState<boolean>(false);
  const [isGranted, setIsGranted] = useState<boolean | null>(null);

  // Check if permission was already requested
  useEffect(() => {
    if (typeof window === 'undefined') return;

    const requested = localStorage.getItem(LOCATION_PERMISSION_KEY);
    if (requested === 'true') {
      setHasRequested(true);
      // Check current permission status
      if (navigator.permissions) {
        navigator.permissions
          .query({ name: 'geolocation' as PermissionName })
          .then(result => {
            setIsGranted(result.state === 'granted');
          })
          .catch(() => {
            // Fallback: try to get position to check permission
            if (navigator.geolocation) {
              navigator.geolocation.getCurrentPosition(
                () => setIsGranted(true),
                () => setIsGranted(false),
                { timeout: 100, maximumAge: Infinity }
              );
            }
          });
      }
    }
  }, []);

  const requestPermission = () => {
    if (hasRequested) return;

    if (!navigator.geolocation) {
      console.log('Geolocation is not supported by this browser');
      return;
    }

    // Mark as requested
    localStorage.setItem(LOCATION_PERMISSION_KEY, 'true');
    setHasRequested(true);

    // Request location permission
    navigator.geolocation.getCurrentPosition(
      // Success callback - permission granted
      position => {
        console.log('Location permission granted:', position.coords);
        setIsGranted(true);
        // You can use the position data here if needed
      },
      // Error callback - permission denied or error
      error => {
        if (error.code === error.PERMISSION_DENIED) {
          console.log('Location permission denied by user');
          setIsGranted(false);
        } else {
          console.log('Error getting location:', error.message);
          setIsGranted(false);
        }
      },
      {
        enableHighAccuracy: false,
        timeout: 5000,
        maximumAge: 0,
      }
    );
  };

  return {
    hasRequested,
    isGranted,
    requestPermission,
  };
}
